import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LOCKED_SERVER_URL, xtreamAPI } from '@/services/xtream-api';
import { branding } from '@/config/branding';
import type {
  XtreamCredentials,
  XtreamLiveStream,
  XtreamVodStream,
  XtreamSeries,
  XtreamCategory,
  XtreamAuthResponse,
} from '@/types/xtream';

interface AppState {
  // Authentication
  isAuthenticated: boolean;
  credentials: XtreamCredentials | null;
  authInfo: XtreamAuthResponse | null;
  setCredentials: (credentials: XtreamCredentials) => void;
  setAuthInfo: (authInfo: XtreamAuthResponse) => void;
  logout: () => void;

  // Operator / Admin settings (editable at runtime, persisted)
  serverUrl: string;
  brandName: string;
  subscribeUrl: string;
  resellerMode: boolean;
  showcaseUsername: string;
  showcasePassword: string;
  adminPanelOpen: boolean;
  /** Admin-editable provider URL overrides, keyed by provider id (A/B/C). */
  providerUrls: Record<string, string>;
  setProviderUrl: (id: string, url: string) => void;
  /** When false, the login "use my own provider" (BYO) option is disabled. */
  allowOwnProvider: boolean;
  setAllowOwnProvider: (on: boolean) => void;
  setServerUrl: (url: string) => void;
  resetServerUrl: () => void;
  setBrandName: (name: string) => void;
  setSubscribeUrl: (url: string) => void;
  setResellerMode: (on: boolean) => void;
  setShowcaseCreds: (username: string, password: string) => void;
  setAdminPanelOpen: (open: boolean) => void;

  // Reseller / guest showcase mode
  isGuest: boolean;
  loginRequested: boolean;
  subscribeGateOpen: boolean;
  enterGuestMode: (credentials: XtreamCredentials) => void;
  exitGuestMode: () => void;
  setLoginRequested: (requested: boolean) => void;
  setSubscribeGateOpen: (open: boolean) => void;
  /** Returns true if playback is allowed; otherwise opens the subscribe gate. */
  requestPlayback: () => boolean;

  // Content
  liveCategories: XtreamCategory[];
  vodCategories: XtreamCategory[];
  seriesCategories: XtreamCategory[];
  liveStreams: XtreamLiveStream[];
  vodStreams: XtreamVodStream[];
  series: XtreamSeries[];

  setLiveCategories: (categories: XtreamCategory[]) => void;
  setVodCategories: (categories: XtreamCategory[]) => void;
  setSeriesCategories: (categories: XtreamCategory[]) => void;
  setLiveStreams: (streams: XtreamLiveStream[]) => void;
  setVodStreams: (streams: XtreamVodStream[]) => void;
  setSeries: (series: XtreamSeries[]) => void;

  // Player
  currentStream: XtreamLiveStream | XtreamVodStream | null;
  currentStreamUrl: string | null;
  isPlaying: boolean;
  setCurrentStream: (stream: XtreamLiveStream | XtreamVodStream | null, url: string | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;

  // MultiView
  multiViewStreams: Array<{ stream: XtreamLiveStream | XtreamVodStream; url: string }>;
  showMultiView: boolean;
  addMultiViewStream: (stream: XtreamLiveStream | XtreamVodStream, url: string) => void;
  removeMultiViewStream: (streamId: number) => void;
  clearMultiView: () => void;
  setShowMultiView: (show: boolean) => void;

  // Favorites
  favorites: number[];
  addFavorite: (streamId: number) => void;
  removeFavorite: (streamId: number) => void;
  isFavorite: (streamId: number) => boolean;
  clearFavorites: () => void;

  // Watch History
  watchHistory: Array<{
    streamId: number;
    streamName: string;
    timestamp: number;
    streamType: 'live' | 'vod' | 'series';
    poster?: string;
    seriesId?: number;
    season?: number;
    episodeNum?: number;
  }>;
  addToHistory: (
    streamId: number,
    streamName: string,
    streamType: 'live' | 'vod' | 'series',
    extra?: { poster?: string; seriesId?: number; season?: number; episodeNum?: number }
  ) => void;
  clearWatchHistory: () => void;

  // Resume playback positions (keyed by `${streamType}:${streamId}`)
  resumePositions: Record<string, { position: number; duration: number; updatedAt: number }>;
  setResumePosition: (key: string, position: number, duration: number) => void;
  getResumePosition: (key: string) => number | undefined;
  clearResumePosition: (key: string) => void;

  // Last watched episode per series (for series "Resume"/auto-next)
  lastEpisodeBySeries: Record<number, { season: number; episodeNum: number; episodeId: string }>;
  setLastEpisode: (seriesId: number, info: { season: number; episodeNum: number; episodeId: string }) => void;

  // Auto-play-next handler, registered by the series view while an episode plays
  nextEpisodeHandler: (() => void) | null;
  setNextEpisodeHandler: (handler: (() => void) | null) => void;

  // Movie detail modal target
  detailVod: XtreamVodStream | null;
  setDetailVod: (vod: XtreamVodStream | null) => void;

  // Series detail requested from another view (e.g. Home rails)
  requestedSeriesId: number | null;
  setRequestedSeriesId: (seriesId: number | null) => void;

  // Global search overlay
  globalSearchOpen: boolean;
  setGlobalSearchOpen: (open: boolean) => void;
  recentSearches: string[];
  addRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;

  // UI State
  sidebarOpen: boolean;
  currentView: 'home' | 'live' | 'vod' | 'series' | 'favorites' | 'settings' | 'epg';
  selectedCategory: string | null;
  searchQuery: string;
  vodSort: 'newest' | 'oldest' | 'az' | 'za' | 'rating';
  theme: 'light' | 'dark';
  accentColor: string; // Theme accent color
  channelViewMode: 'grid' | 'list' | 'tile'; // View mode for channel lists

  setSidebarOpen: (open: boolean) => void;
  setCurrentView: (view: 'home' | 'live' | 'vod' | 'series' | 'favorites' | 'settings' | 'epg') => void;
  setSelectedCategory: (categoryId: string | null) => void;
  setSearchQuery: (query: string) => void;
  setVodSort: (sort: 'newest' | 'oldest' | 'az' | 'za' | 'rating') => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setAccentColor: (color: string) => void;
  setChannelViewMode: (mode: 'grid' | 'list' | 'tile') => void;

  // EPG State
  epgOverlayOpen: boolean;
  setEpgOverlayOpen: (open: boolean) => void;
  epgOffsetMinutes: number;
  epgOffsetAuto: boolean; // when true, offset is auto-derived from the provider's server clock
  setEpgOffsetMinutes: (offset: number) => void; // manual override (sets epgOffsetAuto = false)
  autoSetEpgOffset: (offset: number) => void; // auto-derived update (keeps epgOffsetAuto = true)
  setEpgOffsetAuto: (auto: boolean) => void;

  // Picture-in-Picture State
  pipEnabled: boolean;
  pipPosition: { x: number; y: number };
  setPipEnabled: (enabled: boolean) => void;
  setPipPosition: (position: { x: number; y: number }) => void;

  // Preloading State
  isPreloading: boolean;
  preloadProgress: { live: boolean; vod: boolean; series: boolean };
  setIsPreloading: (isPreloading: boolean) => void;
  setPreloadProgress: (progress: { live: boolean; vod: boolean; series: boolean }) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Authentication
      isAuthenticated: false,
      credentials: null,
      authInfo: null,
      setCredentials: (credentials) =>
        set({ credentials, isAuthenticated: true, isGuest: false, loginRequested: false }),
      setAuthInfo: (authInfo) => set({ authInfo }),

      // Operator / Admin settings (defaults from env/locked URL, then persisted)
      serverUrl: LOCKED_SERVER_URL,
      brandName: branding.brandName,
      subscribeUrl: branding.subscribeUrl,
      resellerMode: branding.resellerMode,
      showcaseUsername: branding.showcase.username,
      showcasePassword: branding.showcase.password,
      adminPanelOpen: false,
      // Per-provider URL overrides (admin-editable RedHood A/B/C). Empty = use the
      // built-in default from src/config/providers.ts.
      providerUrls: {},
      setProviderUrl: (id, url) =>
        set((state) => ({ providerUrls: { ...state.providerUrls, [id]: url } })),
      allowOwnProvider: false,
      setAllowOwnProvider: (on) => set({ allowOwnProvider: on }),
      setServerUrl: (url) =>
        set({
          serverUrl: url,
          // Drop loaded catalog so it refetches from the new server.
          liveStreams: [],
          vodStreams: [],
          series: [],
          liveCategories: [],
          vodCategories: [],
          seriesCategories: [],
          currentStream: null,
          currentStreamUrl: null,
        }),
      resetServerUrl: () => set({ serverUrl: LOCKED_SERVER_URL }),
      setBrandName: (name) => set({ brandName: name }),
      setSubscribeUrl: (url) => set({ subscribeUrl: url }),
      setResellerMode: (on) => set({ resellerMode: on }),
      setShowcaseCreds: (username, password) =>
        set({ showcaseUsername: username, showcasePassword: password }),
      setAdminPanelOpen: (open) => set({ adminPanelOpen: open }),

      // Reseller / guest showcase mode
      isGuest: false,
      loginRequested: false,
      subscribeGateOpen: false,
      enterGuestMode: (credentials) =>
        set({ credentials, isGuest: true, isAuthenticated: false, loginRequested: false }),
      exitGuestMode: () =>
        set({
          isGuest: false,
          credentials: null,
          liveStreams: [],
          vodStreams: [],
          series: [],
          liveCategories: [],
          vodCategories: [],
          seriesCategories: [],
          currentStream: null,
          currentStreamUrl: null,
          detailVod: null,
          globalSearchOpen: false,
          subscribeGateOpen: false,
        }),
      setLoginRequested: (requested) => set({ loginRequested: requested }),
      setSubscribeGateOpen: (open) => set({ subscribeGateOpen: open }),
      requestPlayback: () => {
        const { isAuthenticated, isGuest } = get();
        if (isAuthenticated && !isGuest) return true;
        set({ subscribeGateOpen: true });
        return false;
      },
      logout: () => {
        // Clear saved credentials from localStorage (Remember Me feature)
        localStorage.removeItem('redhood_saved_credentials');

        // Also clear the API singleton: otherwise its credentials/authInfo and any
        // bring-your-own-provider URL persist after sign-out, so the next user on a
        // shared machine could reuse the previous user's line before a reload.
        xtreamAPI.logout();
        xtreamAPI.setServerUrl(LOCKED_SERVER_URL);

        // Reset authentication state
        set({
          isAuthenticated: false,
          credentials: null,
          authInfo: null,
          liveStreams: [],
          vodStreams: [],
          series: [],
          liveCategories: [],
          vodCategories: [],
          seriesCategories: [],
          currentStream: null,
          currentStreamUrl: null,
          isPlaying: false,
          currentView: 'home',
          selectedCategory: null,
          searchQuery: '',
          detailVod: null,
          globalSearchOpen: false,
          isGuest: false,
          loginRequested: false,
          subscribeGateOpen: false,
          // Clear any bring-your-own-provider URL so a shared machine doesn't
          // strand the next user on a previous user's line.
          serverUrl: LOCKED_SERVER_URL,
        });
      },

      // Content
      liveCategories: [],
      vodCategories: [],
      seriesCategories: [],
      liveStreams: [],
      vodStreams: [],
      series: [],

      setLiveCategories: (categories) => set({ liveCategories: categories }),
      setVodCategories: (categories) => set({ vodCategories: categories }),
      setSeriesCategories: (categories) => set({ seriesCategories: categories }),
      setLiveStreams: (streams) => set({ liveStreams: streams }),
      setVodStreams: (streams) => set({ vodStreams: streams }),
      setSeries: (series) => set({ series }),

      // Player
      currentStream: null,
      currentStreamUrl: null,
      isPlaying: false,
      setCurrentStream: (stream, url) => set({ currentStream: stream, currentStreamUrl: url }),
      setIsPlaying: (isPlaying) => set({ isPlaying }),

      // MultiView (up to 4 tiles)
      multiViewStreams: [],
      showMultiView: false,
      addMultiViewStream: (stream, url) => {
        // Guests can't watch — funnel to the subscribe gate instead.
        if (!get().requestPlayback()) return;
        const existing = get().multiViewStreams;
        // Avoid duplicates and cap at 4
        const filtered = existing.filter((s) => s.stream.stream_id !== stream.stream_id);
        const next = [...filtered, { stream, url }].slice(-4);
        set({ multiViewStreams: next, showMultiView: true });
      },
      removeMultiViewStream: (streamId) => {
        const next = get().multiViewStreams.filter((s) => s.stream.stream_id !== streamId);
        set({ multiViewStreams: next, showMultiView: next.length > 0 });
      },
      clearMultiView: () => set({ multiViewStreams: [], showMultiView: false }),
      setShowMultiView: (show) => set({ showMultiView: show }),

      // Favorites
      favorites: [],
      addFavorite: (streamId) => {
        const { favorites } = get();
        if (!favorites.includes(streamId)) {
          set({ favorites: [...favorites, streamId] });
        }
      },
      removeFavorite: (streamId) => {
        const { favorites } = get();
        set({ favorites: favorites.filter((id) => id !== streamId) });
      },
      isFavorite: (streamId) => {
        const { favorites } = get();
        return favorites.includes(streamId);
      },
      clearFavorites: () => set({ favorites: [] }),

      // Watch History
      watchHistory: [],
      addToHistory: (streamId, streamName, streamType, extra) => {
        const { watchHistory } = get();
        const newEntry = {
          streamId,
          streamName,
          streamType,
          timestamp: Date.now(),
          ...extra,
        };
        // Keep only last 50 items
        const updatedHistory = [
          newEntry,
          ...watchHistory.filter((item) => item.streamId !== streamId),
        ].slice(0, 50);
        set({ watchHistory: updatedHistory });
      },
      clearWatchHistory: () => set({ watchHistory: [] }),

      // Resume playback positions
      resumePositions: {},
      setResumePosition: (key, position, duration) => {
        // Ignore tiny positions and finished content (>95% watched)
        if (!key || position < 5) return;
        const { resumePositions } = get();
        if (duration > 0 && position / duration > 0.95) {
          if (!resumePositions[key]) return;
          const next = { ...resumePositions };
          delete next[key];
          set({ resumePositions: next });
          return;
        }
        set({
          resumePositions: {
            ...resumePositions,
            [key]: { position, duration, updatedAt: Date.now() },
          },
        });
      },
      getResumePosition: (key) => get().resumePositions[key]?.position,
      clearResumePosition: (key) => {
        const { resumePositions } = get();
        if (!resumePositions[key]) return;
        const next = { ...resumePositions };
        delete next[key];
        set({ resumePositions: next });
      },

      // Last watched episode per series
      lastEpisodeBySeries: {},
      setLastEpisode: (seriesId, info) =>
        set((state) => ({
          lastEpisodeBySeries: { ...state.lastEpisodeBySeries, [seriesId]: info },
        })),

      // Auto-play-next handler
      nextEpisodeHandler: null,
      setNextEpisodeHandler: (handler) => set({ nextEpisodeHandler: handler }),

      // Movie detail modal
      detailVod: null,
      setDetailVod: (vod) => set({ detailVod: vod }),

      // Series detail requested from another view
      requestedSeriesId: null,
      setRequestedSeriesId: (seriesId) => set({ requestedSeriesId: seriesId }),

      // Global search overlay
      globalSearchOpen: false,
      setGlobalSearchOpen: (open) => set({ globalSearchOpen: open }),
      recentSearches: [],
      addRecentSearch: (query) => {
        const q = query.trim();
        if (q.length < 2) return;
        const existing = get().recentSearches.filter((r) => r.toLowerCase() !== q.toLowerCase());
        set({ recentSearches: [q, ...existing].slice(0, 8) });
      },
      clearRecentSearches: () => set({ recentSearches: [] }),

      // UI State
      sidebarOpen: true,
      currentView: 'home',
      selectedCategory: null,
      searchQuery: '',
      vodSort: 'newest',
      theme: 'dark',
      accentColor: '#ef4444', // Default red accent
      channelViewMode: 'grid', // Default to grid view

      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      // Switching top-level views clears the category + search filters so each
      // view opens to its category hub (a stale search would silently skip it).
      setCurrentView: (view) => set({ currentView: view, selectedCategory: null, searchQuery: '' }),
      setSelectedCategory: (categoryId) => set({ selectedCategory: categoryId }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      setVodSort: (sort) => set({ vodSort: sort }),
      setTheme: (theme) => set({ theme }),
      setAccentColor: (color) => set({ accentColor: color }),
      setChannelViewMode: (mode) => set({ channelViewMode: mode }),

      // EPG State
      epgOverlayOpen: false,
      setEpgOverlayOpen: (open) => set({ epgOverlayOpen: open }),
      epgOffsetMinutes: -300, // Default to EST (GMT-05:00); auto-corrected from the provider on login
      epgOffsetAuto: true,
      setEpgOffsetMinutes: (offset) => {
        const clamped = Math.max(-720, Math.min(720, offset));
        set({ epgOffsetMinutes: clamped, epgOffsetAuto: false });
      },
      autoSetEpgOffset: (offset) => {
        const clamped = Math.max(-720, Math.min(720, offset));
        set({ epgOffsetMinutes: clamped });
      },
      setEpgOffsetAuto: (auto) => set({ epgOffsetAuto: auto }),

      // Picture-in-Picture State
      pipEnabled: false,
      pipPosition: { x: 20, y: 20 }, // Default top-left with padding
      setPipEnabled: (enabled) => set({ pipEnabled: enabled }),
      setPipPosition: (position) => set({ pipPosition: position }),

      // Preloading State
      isPreloading: false,
      preloadProgress: { live: false, vod: false, series: false },
      setIsPreloading: (isPreloading) => set({ isPreloading }),
      setPreloadProgress: (progress) => set({ preloadProgress: progress }),
    }),
    {
      name: 'redhood-iptv-storage',
      partialize: (state) => ({
        // Never persist showcase credentials granted during guest browsing —
        // a reload should return to the storefront/login, not auto-auth as the demo line.
        credentials: state.isGuest ? null : state.credentials,
        favorites: state.favorites,
        watchHistory: state.watchHistory,
        resumePositions: state.resumePositions,
        lastEpisodeBySeries: state.lastEpisodeBySeries,
        theme: state.theme,
        accentColor: state.accentColor,
        channelViewMode: state.channelViewMode,
        pipPosition: state.pipPosition,
        epgOffsetMinutes: state.epgOffsetMinutes,
        epgOffsetAuto: state.epgOffsetAuto,
        serverUrl: state.serverUrl,
        providerUrls: state.providerUrls,
        allowOwnProvider: state.allowOwnProvider,
        brandName: state.brandName,
        subscribeUrl: state.subscribeUrl,
        resellerMode: state.resellerMode,
        showcaseUsername: state.showcaseUsername,
        showcasePassword: state.showcasePassword,
        recentSearches: state.recentSearches,
      }),
    }
  )
);
